import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import throttle from 'lodash/throttle';

import './index.less';

import {
  ACTIVE_ITEM_CLASS_NAME,
  ITEM_CLASS_NAME,
  LIST_CLASS_NAME,
  MAIN_CLASS_NAME,
  NAV_CLASS_NAME,
  SECTION_CLASS_NAME,
  SECTIONS_CLASS_NAME,
  DISPLAY_NAV_DEFAULT,
  NAV_POSITION_DEFAULT,
  SCROLL_THROTTLE_MS,
  SCROLL_ANIMATION_DURATION_MS,
} from './const';

export interface IIbScrollSpySection {
  id: string;
  title: string;
  content: ReactNode;
}

export interface IIbScrollSpyProps {
  displayNav?: boolean;
  navPosition?: 'left' | 'right';
  sections: IIbScrollSpySection[];
}

const IbScrollSpy: React.FC<IIbScrollSpyProps> = ({
  sections,
  displayNav = DISPLAY_NAV_DEFAULT,
  navPosition = NAV_POSITION_DEFAULT,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Indicates that scroll was initiated by clicking on a nav item.
  const isManuallyScrollingRef = useRef(false);

  // Timer object for temporarily disabling detection of the active item during nav-clicking scroll.
  const manualScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const [activeSectionId, setActiveSectionId] = useState(sections[0]?.id);

  const handleScroll = useCallback(
    throttle(() => {
      if (!scrollContainerRef.current || isManuallyScrollingRef.current) {
        return;
      }

      const container = scrollContainerRef.current;
      const { top: containerTop, bottom: containerBottom } = container.getBoundingClientRect();

      let bestId = sections[0].id;
      let bestRatio = 0;
      let firstFullVisibleId: string | null = null;

      sections.forEach(({ id: sectionId }) => {
        const sectionElement = sectionRefs.current[sectionId];
        if (!sectionElement) {
          return;
        }

        const {
          top: elementTop,
          bottom: elementBottom,
          height: elementHeight,
        } = sectionElement.getBoundingClientRect();

        const visibleTop = Math.max(elementTop, containerTop);
        const visibleBottom = Math.min(elementBottom, containerBottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const ratio = visibleHeight / elementHeight;

        if (ratio === 1 && firstFullVisibleId === null) {
          firstFullVisibleId = sectionId;
        }

        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestId = sectionId;
        }
      });

      const calcualtedSectionId = firstFullVisibleId || bestId;
      if (calcualtedSectionId !== activeSectionId) {
        setActiveSectionId(calcualtedSectionId);
      }
    }, SCROLL_THROTTLE_MS),
    [activeSectionId, sections]
  );

  const resetActiveItem = () => {
    handleScroll();
  };

  useEffect(resetActiveItem, [sections]);

  const onItemClick = (id: string) => () => {
    setActiveSectionId(id);

    isManuallyScrollingRef.current = true;
    if (manualScrollTimerRef.current) {
      clearTimeout(manualScrollTimerRef.current);
    }

    const sectionElement = sectionRefs.current[id];
    if (sectionElement && scrollContainerRef.current) {
      sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      manualScrollTimerRef.current = setTimeout(() => {
        isManuallyScrollingRef.current = false;
      }, SCROLL_ANIMATION_DURATION_MS);
    } else {
      isManuallyScrollingRef.current = false;
    }
  };

  const renderNav = () => {
    return (
      <nav className={NAV_CLASS_NAME}>
        <div className={LIST_CLASS_NAME}>
          {sections.map(({ id, title }) => {
            const classNames = [ITEM_CLASS_NAME];
            if (activeSectionId === id) {
              classNames.push(ACTIVE_ITEM_CLASS_NAME);
            }
            return (
              <div key={id} className={classNames.join(' ')} onClick={onItemClick(id)}>
                {title}
              </div>
            );
          })}
        </div>
      </nav>
    );
  };

  const renderSections = () => {
    return (
      <div ref={scrollContainerRef} className={SECTIONS_CLASS_NAME} onScroll={handleScroll}>
        {sections.map(({ id, content }) => {
          return (
            <section
              key={id}
              ref={(el) => {
                sectionRefs.current[id] = el;
              }}
              className={SECTION_CLASS_NAME}
            >
              {content}
            </section>
          );
        })}
      </div>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {displayNav && navPosition === 'left' && renderNav()}
      {renderSections()}
      {displayNav && navPosition === 'right' && renderNav()}
    </div>
  );
};

export default IbScrollSpy;
